import { ColumnDef } from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { ArrowUpDown, ExternalLink, FileText, AlertTriangle } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { type DatasourceBill } from '@/api/datasource-bills-api'

interface GetDatasourceBillsColumnsProps {
  onViewVersions?: (bill: DatasourceBill) => void
}

export function getDatasourceBillsColumns({
  onViewVersions
}: GetDatasourceBillsColumnsProps = {}): ColumnDef<DatasourceBill>[] {

  const formatArrivalTime = (arrivalTime?: string) => {
    if (!arrivalTime) return '—'
    
    try {
      const date = new Date(arrivalTime)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return '—'
    }
  }

  return [
    {
      accessorKey: "bill_number",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Bill Number
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="font-mono font-medium">
            {bill.bill_number}
          </div>
        )
      },
    },
    {
      accessorKey: "title",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Title
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="max-w-[300px]">
            <div className="font-medium truncate" title={bill.title}>
              {bill.title}
            </div>
            {bill.synopsis && (
              <div className="text-sm text-muted-foreground line-clamp-2 mt-1" title={bill.synopsis}>
                {bill.synopsis}
              </div>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: "crossfile_bill_number",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Cross-file
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const crossfile = row.original.crossfile_bill_number
        return crossfile ? (
          <span className="font-mono text-sm">{crossfile}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      accessorKey: "arrival_time",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Arrival Time
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="text-sm text-muted-foreground">
            {formatArrivalTime(bill.arrival_time)}
          </div>
        )
      },
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.arrival_time ? new Date(rowA.original.arrival_time).getTime() : 0
        const dateB = rowB.original.arrival_time ? new Date(rowB.original.arrival_time).getTime() : 0
        return dateA - dateB
      },
    },
    {
      accessorKey: "latest_version_arrival",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-auto p-0 font-semibold hover:bg-transparent"
          >
            Amendment Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="text-sm text-muted-foreground">
            {formatArrivalTime(bill.latest_version_arrival)}
          </div>
        )
      },
      sortingFn: (rowA, rowB) => {
        const dateA = rowA.original.latest_version_arrival ? new Date(rowA.original.latest_version_arrival).getTime() : 0
        const dateB = rowB.original.latest_version_arrival ? new Date(rowB.original.latest_version_arrival).getTime() : 0
        return dateA - dateB
      },
    },
    {
      accessorKey: "versions",
      header: "Versions",
      enableSorting: false,
      cell: ({ row }) => {
        const bill = row.original
        const versions = bill.versions || []
        const hasAmendments = versions.some(v => v.amendments_detected === true)
        const amendmentsProcessed = versions.some(v => v.amendments_detected !== undefined)
        
        return (
          <div className="flex items-center justify-center">
            {onViewVersions ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onViewVersions(bill)
                }}
                title="View Versions & Amendments"
                className="p-1"
              >
                <Badge variant="outline" className="cursor-pointer hover:bg-accent flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {versions.length}
                  {hasAmendments && <AlertTriangle className="h-3 w-3 text-red-500" />}
                  {amendmentsProcessed && !hasAmendments && <span className="text-green-500">✓</span>}
                </Badge>
              </Button>
            ) : (
              <Badge variant="outline" className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                {versions.length}
                {hasAmendments && <AlertTriangle className="h-3 w-3 text-red-500" />}
                {amendmentsProcessed && !hasAmendments && <span className="text-green-500">✓</span>}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      id: "pdf",
      header: "PDF",
      enableSorting: false,
      cell: ({ row }) => {
        const bill = row.original
        
        return (
          <div className="text-center">
            {bill.pdf_url ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  window.open(bill.pdf_url, '_blank', 'noopener,noreferrer')
                }}
                title="View PDF"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            ) : (
              <Badge variant="destructive">
                Unavailable
              </Badge>
            )}
          </div>
        )
      },
    },
  ]
}